// apiService.js
import axios from "axios";

// const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const checkAuthState = async () => {
  try {
    const response = await api.get("/saml/check-auth", {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error checking auth state:", error);
    throw error;
  }
};

export const login = async () => {
  try {
    if (process.env.REACT_APP_ENV === "development") {
      window.location.href = `${process.env.REACT_APP_API_URL}/saml/dev-login`;
    } else {
      window.location.href = `${process.env.REACT_APP_API_URL}/saml/login`;
    }
  } catch (error) {
    console.error("Error during login:", error);
    throw error;
  }
};

export const logout = async () => {
  try {
    const response = await api.post(
      "/saml/logout",
      {},
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error("Error logging out:", error);
    throw error;
  }
};

export const fetchProjects = async () => {
  try {
    const response = await api.get("/project/projects", {
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error("Failed to fetch projects", error);
    throw error;
  }
};

// Admin-only: compare a Tracer project against its Sonderplan record. Returns
// { sonderFound, sonderId, sonderLink, diffs: [{ field, tracerValue, sonderValue }] }.
export const verifyProjectAgainstSonder = async (projectId) => {
  try {
    const response = await api.get(`/project/${projectId}/verify-sonder`, {
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error("Failed to verify project against Sonder", error);
    throw error;
  }
};

// Admin-only: apply the given Sonder-sourced fields onto the Tracer project
// (re-running the edit automation's downstream renames when code/name changes).
export const repairProjectFromSonder = async (projectId, fields) => {
  try {
    const response = await api.post(
      `/project/${projectId}/repair-sonder`,
      { fields },
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to repair project from Sonder", error);
    throw error;
  }
};

// ---- Time Sync ----

export const fetchTimeSyncUserMap = async () => {
  try {
    const response = await api.get("/time-sync/user-map", {
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error("Failed to fetch time-sync user map", error);
    throw error;
  }
};

// Read-only preview: per-person breakdown of who tracked time on a project in
// a date range, with each person's total and whether they're syncable. Powers
// the people step before a real sync — no writes happen. Pass allTime: true
// instead of dates for the project's entire history.
export const fetchTimeSyncBreakdown = async ({
  projectId,
  startDate,
  endDate,
  allTime,
}) => {
  if (!projectId) {
    throw new Error("A project must be selected to load time.");
  }
  try {
    const response = await api.get(
      `/time-sync/project/${projectId}/breakdown`,
      {
        params: allTime ? { allTime: true } : { startDate, endDate },
        withCredentials: true,
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Failed to load time breakdown", error);
    throw error;
  }
};

// Read-only preview for the period-first flow: every project with tracked time
// in the range (matched to Tracer projects), each with its own per-person
// breakdown. No writes happen.
export const fetchTimeSyncPeriodBreakdown = async ({ startDate, endDate }) => {
  try {
    const response = await api.get("/time-sync/period-breakdown", {
      params: { startDate, endDate },
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error("Failed to load period breakdown", error);
    throw error;
  }
};

// Runs a time sync for a single project. A projectId is required — syncing all
// projects at once is intentionally not exposed from the client.
export const runTimeSync = async ({ projectId, ...payload }) => {
  if (!projectId) {
    throw new Error("A project must be selected to run a time sync.");
  }
  try {
    const response = await api.post(
      `/time-sync/project/${projectId}`,
      payload,
      { withCredentials: true },
    );
    return response.data.data;
  } catch (error) {
    console.error("Time sync failed", error);
    throw error;
  }
};

// ---- Time Sync locks ----
// Locked periods refuse all live syncs overlapping them. Any time-sync user
// can view; locking/unlocking is admin-only (enforced server-side).

export const fetchTimeSyncLocks = async ({ includeInactive = false } = {}) => {
  try {
    const response = await api.get("/time-sync/locks", {
      params: includeInactive ? { includeInactive: true } : {},
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error("Failed to fetch time-sync locks", error);
    throw error;
  }
};

export const createTimeSyncLock = async ({ startDate, endDate, reason }) => {
  try {
    const response = await api.post(
      "/time-sync/locks",
      { startDate, endDate, reason },
      { withCredentials: true },
    );
    return response.data.data;
  } catch (error) {
    console.error("Failed to create time-sync lock", error);
    throw error;
  }
};

export const unlockTimeSyncLock = async (lockId) => {
  try {
    const response = await api.delete(`/time-sync/locks/${lockId}`, {
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error("Failed to unlock time-sync lock", error);
    throw error;
  }
};

// Runs a time sync for an explicit selection of projects (period-first flow).
export const runTimeSyncBatch = async ({ projectIds, ...payload }) => {
  if (!Array.isArray(projectIds) || !projectIds.length) {
    throw new Error("Select at least one project to sync.");
  }
  try {
    const response = await api.post(
      "/time-sync/batch",
      { projectIds, ...payload },
      { withCredentials: true },
    );
    return response.data.data;
  } catch (error) {
    console.error("Batch time sync failed", error);
    throw error;
  }
};

export const fetchSettings = async () => {
  try {
    const response = await api.get("/setting/settings", {
      withCredentials: true,
    });
    return response.data.data[0];
  } catch (error) {
    console.error("Failed to fetch settings", error);
    throw error;
  }
};

export const createAsset = async (assetData) => {
  console.log("assetData", assetData);
  try {
    const response = await api.post("/asset/create-asset", assetData, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data.data;
  } catch (error) {
    console.error("Failed to create asset", error);
    throw error;
  }
};

export const editAsset = async (assetId, assetData) => {
  try {
    const response = await api.post(`/asset/edit-asset/${assetId}`, assetData, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to edit asset with id ${assetId}`, error);
    throw error;
  }
};

export const fetchAssetsByProject = async (projectId) => {
  try {
    const response = await api.get(`/asset/project/${projectId}`, {
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to fetch assets for project ${projectId}`, error);
    throw error;
  }
};

export const fetchAllAssets = async () => {
  try {
    const response = await api.get(`/asset`, { withCredentials: true });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to fetch ALL assets`, error);
    throw error;
  }
};

export const fetchAsset = async (assetId) => {
  try {
    const response = await api.get(`/asset/${assetId}`, {
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to fetch asset ${assetId}`, error);
    throw error;
  }
};

export const addNoteToAsset = async (assetId, note) => {
  try {
    const response = await api.post(`/asset/${assetId}/addNote`, note, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to add note to asset with id ${assetId}`, error);
    throw error;
  }
};

// Change an asset's numbering (vandalId / archiveId / keyNumber).
// Requires admin or canManageAssets.
export const renumberAsset = async (assetId, numbering) => {
  try {
    const response = await api.patch(`/asset/${assetId}/number`, numbering, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to renumber asset with id ${assetId}`, error);
    throw error;
  }
};

// Soft-deletes an asset (it can be restored). Requires admin or canManageAssets.
export const deleteAsset = async (assetId) => {
  try {
    const response = await api.delete(`/asset/${assetId}`, {
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to delete asset with id ${assetId}`, error);
    throw error;
  }
};

// Restores a soft-deleted asset. Requires admin or canManageAssets.
export const restoreAsset = async (assetId) => {
  try {
    const response = await api.patch(
      `/asset/${assetId}/restore`,
      {},
      { withCredentials: true }
    );
    return response.data.data;
  } catch (error) {
    console.error(`Failed to restore asset with id ${assetId}`, error);
    throw error;
  }
};

// Fetches all assets including soft-deleted ones (for the manager-only
// deleted-assets view).
export const fetchAllAssetsIncludingDeleted = async () => {
  try {
    const response = await api.get(`/asset?includeDeleted=true`, {
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to fetch assets (including deleted)`, error);
    throw error;
  }
};

export const addHistoryToAsset = async (assetId, history) => {
  try {
    const response = await api.post(`/asset/${assetId}/addHistory`, history, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to add history to asset with id ${assetId}`, error);
    throw error;
  }
};
